import { DEFAULT_DECIMAL_SEPARATOR } from '../../constants/defaultDecimalSeparator'
import { LocaleSeparators } from '../../types/LocaleSeparators'
import { isDigitCharCode } from './isDigitCharCode'
import { SeparatorCounts } from './types/separatorCounts'
import { SeparatorMetadata } from './types/separatorMetadata'
import { SeparatorPositions } from './types/separatorPositions'

/**
 * Heuristically determines which separator is decimal and which (if any) is thousand.
 *
 * Note: we're trying to return a value as soon as possible and use mostly for loops
 * for performance reasons - these helpers might be fired quite often
 */
export const resolveSeparators = (
  input: string,
  separatorCounts: SeparatorCounts,
  separatorPositions: SeparatorPositions,
  locale?: LocaleSeparators,
): LocaleSeparators => {
  const uniqueSeparators = Object.keys(separatorCounts)

  // Case: no separators defined, fallback to default decimal dot
  if (uniqueSeparators.length === 0) {
    return { decimalSeparator: DEFAULT_DECIMAL_SEPARATOR, thousandSeparator: '' }
  }

  // Case: only one separator
  if (uniqueSeparators.length === 1) {
    const singleSeparator = uniqueSeparators[0]

    // separator matches the one in provided locale
    if (locale && singleSeparator === locale.decimalSeparator) {
      return { decimalSeparator: singleSeparator, thousandSeparator: '' }
    }

    const positions = separatorPositions[singleSeparator]
    const lastIndex = positions[positions.length - 1]

    let digitCountAfter = 0
    let onlyZeros = true

    for (let i = lastIndex + 1; i < input.length; i++) {
      const charCode = input.charCodeAt(i)

      if (isDigitCharCode(charCode)) {
        digitCountAfter++

        if (input[i] !== '0') {
          onlyZeros = false
          break
        }
      }
    }

    // only two digits after the last separator - highly probable it was meant to be a fraction
    if (digitCountAfter <= 2) {
      return { decimalSeparator: singleSeparator, thousandSeparator: '' }
    }

    // only three zeros after the separator and not more than 3 digits to justify more separators - sounds like a thousand separator
    const beforeSeparatorLength = input.length - 1 - digitCountAfter
    if (digitCountAfter === 3 && onlyZeros && beforeSeparatorLength <= 3) {
      return { decimalSeparator: '', thousandSeparator: singleSeparator }
    }

    // default fallback: treat the single separator as the decimal separator
    return { decimalSeparator: singleSeparator, thousandSeparator: '' }
  }

  const separatorMetadata: SeparatorMetadata[] = []

  // Case: multiple separators
  for (const separator of uniqueSeparators) {
    const positions = separatorPositions[separator]
    const lastIndex = positions[positions.length - 1]

    let isLikelyThousand = false

    // multiple occurrences of the separator
    if (positions.length > 1) {
      isLikelyThousand = true

      // if a separator occurs multiple times, there's a fair chance it's a thousand separator
      // but if the distance between the occurrences is not 3 digits, it's probably just bad formatting
      for (let i = 1; i < positions.length; i++) {
        if (positions[i] - positions[i - 1] - 1 !== 3) {
          isLikelyThousand = false
          break
        }
      }
    }

    separatorMetadata.push({
      character: separator,
      isLikelyThousand,
      isLocaleDecimal: locale?.decimalSeparator === separator,
      isLocaleThousand: locale?.thousandSeparator === separator,
      lastIndex,
      positions,
    })
  }

  for (const decimalCandidate of separatorMetadata) {
    if (decimalCandidate.isLocaleDecimal) {
      // if we have a solid decimal candidate, we just search for the different separator to make it the thousand separator
      const thousandCandidate = separatorMetadata.find(
        (separator) => separator.character !== decimalCandidate.character,
      )
      const isLastThousandThreeDigitsAfterDecimal =
        thousandCandidate && decimalCandidate.lastIndex === thousandCandidate.lastIndex - 4

      // covering a case that looks as if separators were used the other way around (e.g. 1.234,5 instead of 1,234.5)
      if (isLastThousandThreeDigitsAfterDecimal) {
        return {
          thousandSeparator: decimalCandidate.character,
          decimalSeparator: thousandCandidate.character,
        }
      }

      return {
        decimalSeparator: decimalCandidate.character,
        thousandSeparator: thousandCandidate?.character || '',
      }
    }
  }

  // determine the last and second last separators used in the string
  let lastSeparator = separatorMetadata[0]
  let secondLastSeparator: SeparatorMetadata | undefined

  for (let i = 1; i < separatorMetadata.length; i++) {
    const current = separatorMetadata[i]

    if (current.lastIndex > lastSeparator.lastIndex) {
      secondLastSeparator = lastSeparator
      lastSeparator = current
    } else if (!secondLastSeparator || current.lastIndex > secondLastSeparator.lastIndex) {
      secondLastSeparator = current
    }
  }

  // assume the last separator is the decimal one and the second to last is a thousand separator
  const decimalSeparator = lastSeparator.character
  const thousandSeparator = secondLastSeparator?.isLikelyThousand ? secondLastSeparator.character : ''

  return { decimalSeparator, thousandSeparator }
}
